import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { TextPlugin } from "gsap/TextPlugin";
import logoSrc from "../assets/logo.png";
import { MarketingNav } from "@/components/layout/MarketingNav";
import {
  ShieldCheck,
  FileText,
  GitBranch,
  BarChart3,
  Sparkles,
  Users,
  ChevronRight,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  Lock,
  Globe,
  Zap,
  Star,
  Building2,
  ClipboardList,
  Network,
  BookOpen,
  Brain,
  Activity,
  Target,
  Flag,
  Search,
  Filter,
  Plus,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger, TextPlugin);

// ─── Colour tokens (mirror the site: deep indigo + purple + lime) ─────────────
const C = {
  navy: "#07081a",
  navyMid: "#0d0f2d",
  navyCard: "#11154a",
  navyLight: "#1a1f5e",
  purple: "#6C63FF",
  purpleDim: "#4B44CC",
  purpleGlow: "rgba(108,99,255,0.18)",
  lime: "#a8e063",
  limeGlow: "rgba(168,224,99,0.15)",
  white: "#ffffff",
  slate: "#c4c9e8",
  dimSlate: "#7880a8",
  border: "rgba(108,99,255,0.2)",
  cardBg: "rgba(17,21,74,0.6)",
  glassBg: "rgba(11,13,40,0.7)",
};

// ─── MOCK UI SNAPSHOTS ────────────────────────────────────────────────────────

const PolicyLibrarySnapshot = () => (
  <div className="w-full rounded-xl overflow-hidden" style={{ background: C.glassBg, border: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}>
    <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
      <BookOpen size={14} style={{ color: C.purple }} />
      <span className="text-xs font-semibold" style={{ color: C.slate }}>Policy Library</span>
      <span className="ml-auto text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: "rgba(108,99,255,0.15)", color: C.purple }}>31 Policies</span>
    </div>
    <div className="p-3 space-y-2">
      {[
        { name: "Data Privacy Policy", status: "Approved", cat: "Compliance", color: C.lime },
        { name: "Remote Work Policy", status: "Under Review", cat: "HR", color: "#f59e0b" },
        { name: "Acceptable Use Policy", status: "Approved", cat: "IT Security", color: C.lime },
        { name: "Leave & Absence Policy", status: "Draft", cat: "HR", color: C.dimSlate },
        { name: "Information Security", status: "Pending Approval", cat: "IT", color: "#a78bfa" },
      ].map((p, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg" style={{ background: i === 0 ? "rgba(108,99,255,0.1)" : "transparent" }}>
          <FileText size={13} style={{ color: C.purple, flexShrink: 0 }} />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate" style={{ color: C.white }}>{p.name}</div>
            <div className="text-[10px]" style={{ color: C.dimSlate }}>{p.cat}</div>
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: `${p.color}18`, color: p.color, border: `1px solid ${p.color}30` }}>{p.status}</span>
        </div>
      ))}
    </div>
    <div className="px-3 pb-3 flex gap-2">
      <div className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}` }}>
        <Search size={11} style={{ color: C.dimSlate }} />
        <span className="text-[10px]" style={{ color: C.dimSlate }}>Search policies…</span>
      </div>
      <button className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-semibold" style={{ background: C.purple, color: C.white }}><Plus size={10} /> New</button>
    </div>
  </div>
);

const AICreatorSnapshot = () => (
  <div className="w-full rounded-xl overflow-hidden" style={{ background: C.glassBg, border: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}>
    <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
      <Brain size={14} style={{ color: C.lime }} />
      <span className="text-xs font-semibold" style={{ color: C.slate }}>AI Policy Creator</span>
      <span className="ml-2 flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${C.lime}18`, color: C.lime }}><Sparkles size={9} /> AI-Powered</span>
    </div>
    <div className="p-4 space-y-3">
      <div className="rounded-lg p-3" style={{ background: "rgba(108,99,255,0.08)", border: `1px solid ${C.border}` }}>
        <div className="text-[10px] mb-1" style={{ color: C.dimSlate }}>You</div>
        <div className="text-xs" style={{ color: C.slate }}>Create a remote work policy for a tech company with 200 employees, including security requirements and productivity guidelines.</div>
      </div>
      <div className="rounded-lg p-3" style={{ background: `${C.lime}08`, border: `1px solid ${C.lime}20` }}>
        <div className="flex items-center gap-1 text-[10px] mb-2" style={{ color: C.lime }}><Brain size={9} /><span>ManagEaze AI</span></div>
        <div className="text-xs space-y-1" style={{ color: C.slate }}>
          <div className="font-semibold" style={{ color: C.white }}>Remote Work Policy v1.0</div>
          <div className="text-[10px]">1. Purpose &amp; Scope · 2. Eligibility Criteria · 3. Security Requirements · 4. Data Protection…</div>
        </div>
        <div className="mt-2 flex gap-1">
          {["Draft →", "Review", "Export"].map(a => (
            <button key={a} className="text-[9px] px-2 py-0.5 rounded" style={{ background: "rgba(108,99,255,0.2)", color: C.purple }}>{a}</button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 text-[10px]" style={{ color: C.dimSlate }}>
        <Zap size={9} style={{ color: C.lime }} />
        Generated in 3.2s · Grounded in your company context
      </div>
    </div>
  </div>
);

const ComplianceSnapshot = () => (
  <div className="w-full rounded-xl overflow-hidden" style={{ background: C.glassBg, border: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}>
    <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
      <ShieldCheck size={14} style={{ color: C.lime }} />
      <span className="text-xs font-semibold" style={{ color: C.slate }}>Compliance Dashboard</span>
    </div>
    <div className="p-3 grid grid-cols-2 gap-2 mb-2">
      {[
        { label: "Health Score", value: "86%", color: C.lime, icon: <Activity size={13} /> },
        { label: "Policy Score", value: "56/100", color: "#f59e0b", icon: <Target size={13} /> },
        { label: "Tasks Done", value: "18/21", color: C.purple, icon: <CheckCircle2 size={13} /> },
        { label: "Overdue", value: "1", color: "#f87171", icon: <AlertTriangle size={13} /> },
      ].map((s, i) => (
        <div key={i} className="rounded-lg p-2.5" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}` }}>
          <div className="flex items-center gap-1 mb-1" style={{ color: s.color }}>{s.icon}<span className="text-[9px]">{s.label}</span></div>
          <div className="text-base font-bold" style={{ color: s.color }}>{s.value}</div>
        </div>
      ))}
    </div>
    <div className="px-3 pb-3">
      <div className="text-[10px] mb-2 font-semibold" style={{ color: C.dimSlate }}>Compliance Tasks</div>
      {[
        { t: "Conduct Regular Safety Audits", p: "P2", done: false },
        { t: "Evaluate Training Effectiveness", p: "P2", done: true },
        { t: "Assess Training Needs Annually", p: "P1", done: false },
      ].map((task, i) => (
        <div key={i} className="flex items-center gap-2 py-1.5">
          <div className={`w-3 h-3 rounded-full flex-shrink-0`} style={{ background: task.done ? C.lime : "transparent", border: `2px solid ${task.done ? C.lime : C.dimSlate}` }} />
          <span className="text-[10px] flex-1" style={{ color: task.done ? C.dimSlate : C.slate, textDecoration: task.done ? "line-through" : "none" }}>{task.t}</span>
          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: task.p === "P1" ? "rgba(248,113,113,0.15)" : "rgba(108,99,255,0.15)", color: task.p === "P1" ? "#f87171" : C.purple }}>{task.p}</span>
        </div>
      ))}
    </div>
  </div>
);

const RiskSnapshot = () => (
  <div className="w-full rounded-xl overflow-hidden" style={{ background: C.glassBg, border: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}>
    <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
      <BarChart3 size={14} style={{ color: "#f59e0b" }} />
      <span className="text-xs font-semibold" style={{ color: C.slate }}>Risk Management</span>
      <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full" style={{ background: "rgba(248,113,113,0.15)", color: "#f87171" }}>4 High Risk</span>
    </div>
    <div className="p-3 grid grid-cols-3 gap-2 mb-2">
      {[
        { label: "Total Risks", value: "14", color: C.purple },
        { label: "High Risks", value: "4", color: "#f87171" },
        { label: "Gaps", value: "3", color: "#f59e0b" },
      ].map((s, i) => (
        <div key={i} className="rounded-lg p-2 text-center" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}` }}>
          <div className="text-lg font-bold" style={{ color: s.color }}>{s.value}</div>
          <div className="text-[9px]" style={{ color: C.dimSlate }}>{s.label}</div>
        </div>
      ))}
    </div>
    {/* Mini heat map */}
    <div className="px-3 pb-3">
      <div className="text-[10px] mb-1.5 font-semibold" style={{ color: C.dimSlate }}>Risk Heat Map</div>
      <div className="grid gap-0.5" style={{ gridTemplateColumns: "repeat(5,1fr)" }}>
        {Array.from({ length: 25 }, (_, i) => {
          const row = Math.floor(i / 5);
          const col = i % 5;
          const score = (row + 1) * (col + 1);
          const filled = [3, 7, 11, 16, 19, 21, 23].includes(i);
          const bg = score >= 12 ? "rgba(248,113,113,0.4)" : score >= 8 ? "rgba(245,158,11,0.4)" : "rgba(168,224,99,0.3)";
          return (
            <div key={i} className="rounded-sm flex items-center justify-center aspect-square text-[7px] font-bold transition-all" style={{ background: filled ? bg : `${bg.replace("0.4", "0.12").replace("0.3", "0.1")}`, color: filled ? C.white : "transparent", border: filled ? `1px solid ${bg.replace("0.4", "0.7").replace("0.3", "0.6")}` : "none" }}>
              {filled ? "●" : ""}
            </div>
          );
        })}
      </div>
      <div className="flex gap-3 mt-1.5">
        {[["Low", "rgba(168,224,99,0.4)"], ["Moderate", "rgba(245,158,11,0.4)"], ["Critical", "rgba(248,113,113,0.4)"]].map(([l, c]) => (
          <div key={l} className="flex items-center gap-1 text-[9px]" style={{ color: C.dimSlate }}>
            <div className="w-2 h-2 rounded-sm" style={{ background: c as string }} />{l}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const GovernanceSnapshot = () => (
  <div className="w-full rounded-xl overflow-hidden" style={{ background: C.glassBg, border: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}>
    <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
      <Network size={14} style={{ color: C.purple }} />
      <span className="text-xs font-semibold" style={{ color: C.slate }}>Governance</span>
    </div>
    <div className="p-3 space-y-2">
      {/* Workflow visual */}
      <div className="flex items-center gap-1 text-[9px]">
        {["Draft", "Review", "Approval", "Approved"].map((s, i, arr) => (
          <div key={s} className="flex items-center gap-1">
            <div className="px-2 py-1 rounded font-medium" style={{ background: i <= 2 ? "rgba(108,99,255,0.2)" : `${C.lime}20`, color: i <= 2 ? C.purple : C.lime, border: `1px solid ${i <= 2 ? C.purple : C.lime}40` }}>{s}</div>
            {i < arr.length - 1 && <ChevronRight size={9} style={{ color: C.dimSlate }} />}
          </div>
        ))}
      </div>
      {/* Org chart mini */}
      <div className="rounded-lg p-2.5" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}` }}>
        <div className="text-[10px] mb-2 font-semibold" style={{ color: C.dimSlate }}>Committees</div>
        {[
          { name: "Board Audit Committee", members: 4, status: "Active" },
          { name: "Risk & Compliance", members: 6, status: "Active" },
          { name: "IT Governance", members: 3, status: "Review" },
        ].map((c, i) => (
          <div key={i} className="flex items-center gap-2 py-1">
            <Building2 size={10} style={{ color: C.purple }} />
            <span className="text-[10px] flex-1" style={{ color: C.slate }}>{c.name}</span>
            <span className="text-[9px]" style={{ color: C.dimSlate }}>{c.members} members</span>
          </div>
        ))}
      </div>
      {/* Voting decision */}
      <div className="rounded-lg p-2.5" style={{ background: `${C.lime}06`, border: `1px solid ${C.lime}20` }}>
        <div className="flex items-center gap-2">
          <CheckCircle2 size={12} style={{ color: C.lime }} />
          <div>
            <div className="text-[10px] font-semibold" style={{ color: C.white }}>Policy Approved — Vote 5/5</div>
            <div className="text-[9px]" style={{ color: C.dimSlate }}>Information Security Policy · 2m ago</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const WorkflowSnapshot = () => (
  <div className="w-full rounded-xl overflow-hidden" style={{ background: C.glassBg, border: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}>
    <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
      <GitBranch size={14} style={{ color: C.purple }} />
      <span className="text-xs font-semibold" style={{ color: C.slate }}>Approval Workflow</span>
    </div>
    <div className="p-3 space-y-2">
      {[
        { stage: "Draft Created", by: "Saad H.", time: "2h ago", done: true },
        { stage: "Sent for Review", by: "Meeran F.", time: "1h ago", done: true },
        { stage: "Pending Approval", by: "Faisal C.", time: "30m ago", done: false, current: true },
        { stage: "Final Approval", by: "—", time: "", done: false },
      ].map((s, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="flex flex-col items-center" style={{ minWidth: 24 }}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0`} style={{ background: s.done ? C.lime : s.current ? C.purple : "transparent", border: `2px solid ${s.done ? C.lime : s.current ? C.purple : C.dimSlate}` }}>
              {s.done && <CheckCircle2 size={11} color={C.navy} strokeWidth={3} />}
              {s.current && <div className="w-1.5 h-1.5 rounded-full" style={{ background: C.white }} />}
            </div>
            {i < 3 && <div className="w-px h-4 mt-0.5" style={{ background: s.done ? `${C.lime}50` : `${C.dimSlate}30` }} />}
          </div>
          <div className="pb-2">
            <div className="text-xs font-medium" style={{ color: s.current ? C.white : s.done ? C.slate : C.dimSlate }}>{s.stage}</div>
            {s.by !== "—" && <div className="text-[10px]" style={{ color: C.dimSlate }}>{s.by} · {s.time}</div>}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─── MAIN LANDING PAGE ────────────────────────────────────────────────────────
export default function Landing() {
  const containerRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const heroHeadlineRef = useRef<HTMLDivElement>(null);
  const heroBadgeRef = useRef<HTMLDivElement>(null);
  const heroSubRef = useRef<HTMLParagraphElement>(null);
  const heroCtaRef = useRef<HTMLDivElement>(null);
  const orbitRef = useRef<HTMLDivElement>(null);

  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      id: "policy",
      icon: <BookOpen size={22} />,
      color: C.purple,
      label: "Policy Management",
      headline: "Your entire policy lifecycle, unified.",
      sub: "Create, version, review, and distribute policies with an AI-powered editor and role-based workflows — all in one place.",
      bullets: ["AI draft generation from prompts", "Block-based editor with RAG context", "Multi-stage approval workflows", "Expiry tracking & renewal management"],
      snapshot: <PolicyLibrarySnapshot />,
    },
    {
      id: "ai",
      icon: <Brain size={22} />,
      color: C.lime,
      label: "AI Creator & Agent",
      headline: "Write policies in seconds, not days.",
      sub: "Describe it, and the AI writes it. Grounded in your org's documents, the AI creator generates policy-ready drafts tailored to your context.",
      bullets: ["Natural language policy generation", "Company context via RAG ingestion", "Embedded Q&A agent for all staff", "Instant audit-ready output"],
      snapshot: <AICreatorSnapshot />,
    },
    {
      id: "compliance",
      icon: <ShieldCheck size={22} />,
      color: C.lime,
      label: "Compliance",
      headline: "Know your compliance posture instantly.",
      sub: "Convert approved policies into trackable tasks. Monitor health scores, flag gaps, and keep your compliance posture audit-ready at all times.",
      bullets: ["Compliance health score dashboard", "Task checklists auto-generated from policies", "Priority tracking & overdue alerts", "Per-policy status & renewal visibility"],
      snapshot: <ComplianceSnapshot />,
    },
    {
      id: "risk",
      icon: <AlertTriangle size={22} />,
      color: "#f59e0b",
      label: "Risk Management",
      headline: "See every risk before it becomes a crisis.",
      sub: "Identify, score, and track organisational risks across categories. Visualise your risk landscape with a live heat map and link risks directly to mitigating policies.",
      bullets: ["Risk register with 5×5 scoring matrix", "Interactive heat map visualisation", "Risk-to-policy traceability", "Appetite statements & Three Lines of Defence"],
      snapshot: <RiskSnapshot />,
    },
    {
      id: "governance",
      icon: <Network size={22} />,
      color: C.purple,
      label: "Governance",
      headline: "Structure, accountability, oversight — built in.",
      sub: "Org chart management, committee tracking, voting & decisions, and task automation — the full governance layer for regulated organisations.",
      bullets: ["Committee & org chart management", "Electronic voting & decision logging", "Terms of Reference (TOR) editor", "Meeting minutes & audit trail"],
      snapshot: <GovernanceSnapshot />,
    },
    {
      id: "workflow",
      icon: <GitBranch size={22} />,
      color: C.purple,
      label: "Approval Workflows",
      headline: "Zero policies slip through without approval.",
      sub: "Role-gated routing from Draft to Final Approval. Comment threads, full audit logs, and expiry enforcement build accountability into every policy.",
      bullets: ["Draft → Review → Approval pipeline", "Role-based routing & permissions", "Embedded comments with audit trail", "Deadline & expiry enforcement"],
      snapshot: <WorkflowSnapshot />,
    },
  ];

  const stats = [
    { num: "75%", label: "Faster policy cycle time" },
    { num: "98%", label: "Policy acknowledgment rate" },
    { num: "4×", label: "Compliance tasks closed faster" },
    { num: "100%", label: "Audit-ready, always" },
  ];

  const industries = [
    "Financial Services", "Healthcare", "Legal & Compliance",
    "Technology", "Insurance", "Government", "Education", "Professional Services"
  ];

  // ── GSAP animations ──────────────────────────────────────────────────────
  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero entrance
      const heroTl = gsap.timeline({ delay: 0.1 });
      heroTl
        .from(heroBadgeRef.current, { y: -20, opacity: 0, duration: 0.5, ease: "back.out(1.5)" })
        .from(".hero-line", { y: 60, opacity: 0, duration: 0.7, stagger: 0.15, ease: "power3.out" }, "-=0.2")
        .from(heroSubRef.current, { y: 20, opacity: 0, duration: 0.5, ease: "power2.out" }, "-=0.3")
        .from(heroCtaRef.current, { y: 20, opacity: 0, duration: 0.5, ease: "power2.out" }, "-=0.2")
        .from(".hero-stat", { y: 30, opacity: 0, duration: 0.5, stagger: 0.1, ease: "back.out(1.2)" }, "-=0.1");

      // Floating orbs in hero
      const orbs = gsap.utils.toArray<Element>(".hero-orb");
      orbs.forEach((orb, i) => {
        gsap.to(orb, {
          y: i % 2 === 0 ? -20 : 20,
          duration: 2.5 + i * 0.4,
          yoyo: true,
          repeat: -1,
          ease: "sine.inOut",
          delay: i * 0.3,
        });
      });

      // Scroll-triggered sections
      gsap.utils.toArray<Element>(".scroll-fade").forEach((el) => {
        gsap.from(el, {
          scrollTrigger: { trigger: el, start: "top 85%", toggleActions: "play none none none" },
          y: 50, opacity: 0, duration: 0.8, ease: "power2.out",
        });
      });

      gsap.utils.toArray<Element>(".scroll-stagger").forEach((container) => {
        gsap.from((container as HTMLElement).querySelectorAll(".stagger-child"), {
          scrollTrigger: { trigger: container, start: "top 95%", toggleActions: "play none none none" },
          y: 40, opacity: 0, duration: 0.6, stagger: 0.12, ease: "power2.out",
        });
      });

      // Stats counter animation — fire ONCE, hold final value
      gsap.utils.toArray<Element>(".stat-num").forEach((el) => {
        ScrollTrigger.create({
          trigger: el,
          start: "top 90%",
          once: true, // never re-fires
          onEnter: () => {
            const raw = (el as HTMLElement).dataset.value || "";
            const isPercent = raw.includes("%");
            const isX = raw.includes("×");
            const num = parseFloat(raw);
            if (!isNaN(num)) {
              const obj = { val: 0 };
              gsap.to(obj, {
                val: num,
                duration: 1.8,
                ease: "power2.out",
                onUpdate: () => {
                  (el as HTMLElement).textContent =
                    Math.round(obj.val) + (isPercent ? "%" : isX ? "×" : "");
                },
                onComplete: () => {
                  // lock the final display value permanently
                  (el as HTMLElement).textContent = raw;
                },
              });
            }
          },
        });
      });

      // Glow pulse on CTA
      gsap.to(".cta-glow", {
        boxShadow: `0 0 60px 10px rgba(108,99,255,0.4)`,
        duration: 1.5,
        yoyo: true,
        repeat: -1,
        ease: "sine.inOut",
      });

      // Animate the feature tab indicator
      gsap.to(".feature-snapshot", {
        scrollTrigger: { trigger: ".feature-snapshot", start: "top 80%" },
        opacity: 1, y: 0, duration: 0.7, ease: "power2.out",
      });

    }, containerRef);

    return () => ctx.revert();
  }, []);

  // Animate feature snapshot in on tab change
  useEffect(() => {
    gsap.fromTo(".feature-snapshot-inner", { opacity: 0, y: 16, scale: 0.98 }, { opacity: 1, y: 0, scale: 1, duration: 0.4, ease: "power2.out" });
  }, [activeFeature]);

  const active = features[activeFeature];

  return (
    <div ref={containerRef} style={{ background: C.navy, color: C.white, fontFamily: "'DM Sans', ui-sans-serif, sans-serif", minHeight: "100vh", overflowX: "hidden" }}>

      {/* ─── NAV ─── */}
      <MarketingNav />

      {/* ─── HERO ─── */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 pt-24 overflow-hidden">
        {/* Background orbs */}
        <div className="hero-orb absolute rounded-full pointer-events-none" style={{ width: 500, height: 500, background: "radial-gradient(circle, rgba(108,99,255,0.15) 0%, transparent 70%)", top: "10%", left: "10%", filter: "blur(60px)" }} />
        <div className="hero-orb absolute rounded-full pointer-events-none" style={{ width: 400, height: 400, background: "radial-gradient(circle, rgba(168,224,99,0.1) 0%, transparent 70%)", bottom: "15%", right: "10%", filter: "blur(80px)" }} />
        <div className="hero-orb absolute rounded-full pointer-events-none" style={{ width: 300, height: 300, background: "radial-gradient(circle, rgba(108,99,255,0.08) 0%, transparent 70%)", top: "50%", right: "25%", filter: "blur(50px)" }} />

        {/* Dot grid */}
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(108,99,255,0.15) 1px, transparent 1px)", backgroundSize: "30px 30px", maskImage: "radial-gradient(ellipse 70% 60% at 50% 50%, black, transparent)" }} />

        <div ref={heroBadgeRef} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8 text-xs font-semibold" style={{ background: "rgba(108,99,255,0.12)", border: `1px solid ${C.border}`, color: C.purple }}>
          <Sparkles size={12} />
          <span>Now in Public Beta · AI-First GRC Platform</span>
        </div>

        <h1 className="relative max-w-4xl mx-auto mb-6" style={{ lineHeight: 1.12 }}>
          <div className="hero-line text-6xl md:text-8xl font-black tracking-tighter block" style={{ color: C.white }}>Govern Smarter.</div>
          <div className="hero-line text-6xl md:text-8xl font-black tracking-tighter block" style={{ color: C.white }}>Comply Faster.</div>
          <div className="hero-line text-6xl md:text-8xl font-black tracking-tighter block" style={{ background: "linear-gradient(90deg, #6C63FF, #a8e063)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Scale Without Chaos.</div>
        </h1>

        <p ref={heroSubRef} className="max-w-xl mx-auto text-xl mb-10" style={{ color: C.dimSlate, lineHeight: 1.6 }}>
          The all-in-one GRC platform for <span style={{ color: C.slate }}>Policy Management</span>, <span style={{ color: C.slate }}>Compliance</span>, <span style={{ color: C.slate }}>Risk</span>, and <span style={{ color: C.slate }}>Governance</span> — built for compliance-heavy, fast-moving organisations.
        </p>

        <div ref={heroCtaRef} className="flex flex-col sm:flex-row gap-4 items-center mb-20">
          <button className="cta-glow flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-base transition-all hover:scale-105" style={{ background: C.purple, color: C.white }}>
            Start Free Trial <ArrowRight size={16} />
          </button>
          <button className="flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-base transition-all hover:opacity-80" style={{ border: `1px solid ${C.border}`, color: C.slate }}>
            Request a Demo <ChevronRight size={16} />
          </button>
        </div>

        {/* Hero stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl w-full">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              className="hero-stat rounded-xl px-4 py-4 text-center"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.8 + i * 0.1, ease: "backOut" }}
              style={{ background: C.cardBg, border: `1px solid ${C.border}`, backdropFilter: "blur(8px)" }}
            >
              <div className="stat-num text-4xl font-black mb-1" data-value={s.num} style={{ background: "linear-gradient(135deg, #6C63FF, #a8e063)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                {s.num}
              </div>
              <div className="text-sm" style={{ color: C.dimSlate }}>{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── PLATFORM OVERVIEW / PILLARS ─── */}
      <section className="py-24 px-6 max-w-6xl mx-auto scroll-fade">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ background: `${C.lime}15`, border: `1px solid ${C.lime}30`, color: C.lime }}>
            <Globe size={11} /> Full-Spectrum GRC
          </div>
          <h2 className="text-5xl md:text-6xl font-black tracking-tight mb-4" style={{ color: C.white }}>One platform.<br />Every pillar of GRC.</h2>
          <p className="max-w-xl mx-auto text-lg" style={{ color: C.dimSlate }}>ManagEaze unifies the four foundational layers of Governance, Risk & Compliance — so nothing slips, and everything connects.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: <BookOpen size={24} />, label: "Policy Management", desc: "Create, version, and distribute policies with AI and role-based workflows.", color: C.purple },
            { icon: <ShieldCheck size={24} />, label: "Compliance", desc: "Track tasks, monitor health scores, and stay audit-ready automatically.", color: C.lime },
            { icon: <AlertTriangle size={24} />, label: "Risk Management", desc: "Identify, score, and link risks to policies across every category.", color: "#f59e0b" },
            { icon: <Network size={24} />, label: "Governance", desc: "Manage committees, decisions, and org structures with full accountability.", color: "#a78bfa" },
          ].map((p, i) => (
            <motion.div
              key={i}
              className="rounded-2xl p-6 group cursor-pointer"
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.1, ease: "easeOut" }}
              whileHover={{ y: -4 }}
              style={{ background: C.cardBg, border: `1px solid ${C.border}`, backdropFilter: "blur(8px)" }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all" style={{ background: `${p.color}15`, color: p.color, border: `1px solid ${p.color}30` }}>
                {p.icon}
              </div>
              <h3 className="font-bold text-lg mb-2" style={{ color: C.white }}>{p.label}</h3>
              <p className="text-base leading-relaxed" style={{ color: C.dimSlate }}>{p.desc}</p>
              <div className="flex items-center gap-1 mt-4 text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: p.color }}>
                Explore <ChevronRight size={12} />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── FEATURE DEEP DIVE (tabbed) ─── */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-12 scroll-fade">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ background: `${C.purple}15`, border: `1px solid ${C.border}`, color: C.purple }}>
            <Zap size={11} /> Feature Breakdown
          </div>
          <h2 className="text-5xl md:text-6xl font-black tracking-tight mb-4" style={{ color: C.white }}>Built for every role,<br />every workflow.</h2>
        </div>

        {/* Feature tab pills */}
        <div className="flex flex-wrap gap-2 justify-center mb-12 scroll-fade">
          {features.map((f, i) => (
            <button
              key={f.id}
              onClick={() => setActiveFeature(i)}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200"
              style={i === activeFeature
                ? { background: f.color, color: C.navy, fontWeight: 700 }
                : { background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, color: C.dimSlate }
              }
            >
              <span style={{ color: i === activeFeature ? C.navy : f.color }}>{f.icon}</span>
              {f.label}
            </button>
          ))}
        </div>

        {/* Feature content */}
        <div className="feature-snapshot grid md:grid-cols-2 gap-10 items-center">
          {/* Copy side */}
          <div className="feature-snapshot-inner">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ background: `${active.color}15`, color: active.color, border: `1px solid ${active.color}30` }}>
              {active.icon}
            </div>
            <h3 className="text-4xl md:text-5xl font-black mb-4 tracking-tight" style={{ color: C.white }}>{active.headline}</h3>
            <p className="text-lg mb-8 leading-relaxed" style={{ color: C.dimSlate }}>{active.sub}</p>
            <ul className="space-y-3">
              {active.bullets.map((b, i) => (
                <li key={i} className="flex items-center gap-3 text-base" style={{ color: C.slate }}>
                  <CheckCircle2 size={16} style={{ color: active.color, flexShrink: 0 }} />
                  {b}
                </li>
              ))}
            </ul>
            <button className="mt-10 flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all hover:opacity-80" style={{ background: `${active.color}18`, color: active.color, border: `1px solid ${active.color}30` }}>
              See {active.label} in action <ArrowRight size={14} />
            </button>
          </div>

          {/* Snapshot side */}
          <div className="feature-snapshot-inner" style={{ minHeight: 420 }}>
            {active.snapshot}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="py-24 px-6 max-w-5xl mx-auto scroll-fade">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ background: `${C.purple}15`, border: `1px solid ${C.border}`, color: C.purple }}>
            <TrendingUp size={11} /> How It Works
          </div>
          <h2 className="text-5xl md:text-6xl font-black tracking-tight mb-4" style={{ color: C.white }}>From policy creation<br />to full compliance posture.</h2>
        </div>

        <div className="relative">
          <div className="space-y-6">
            {[
              { step: "01", icon: <Brain size={18} />, title: "Create with AI", desc: "Describe the policy you need. ManagEaze's AI generates a structured, company-grounded draft in seconds.", color: C.purple },
              { step: "02", icon: <GitBranch size={18} />, title: "Route for Approval", desc: "Assign reviewers and approvers. The workflow enforces every stage with comments, deadlines, and audit logging.", color: "#a78bfa" },
              { step: "03", icon: <ShieldCheck size={18} />, title: "Track Compliance", desc: "Approved policies auto-generate compliance tasks. Monitor your health score and close tasks against obligations.", color: C.lime },
              { step: "04", icon: <AlertTriangle size={18} />, title: "Manage Risk", desc: "Link policies to the risks they mitigate. Score and visualise your risk landscape. Flag exposure before it escalates.", color: "#f59e0b" },
              { step: "05", icon: <Network size={18} />, title: "Govern with Confidence", desc: "Committees, org structures, and voting decisions — all captured and audit-ready. The board-level view of your GRC posture.", color: C.purple },
            ].map((s, i, arr) => (
              <motion.div
                key={i}
                className="flex gap-6 items-start"
                initial={{ x: -30, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.08, ease: "easeOut" }}
              >
                {/* Step icon + connector line */}
                <div className="flex flex-col items-center flex-shrink-0" style={{ width: 48 }}>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: `${s.color}18`, border: `1px solid ${s.color}30`, color: s.color }}>
                    {s.icon}
                  </div>
                  {i < arr.length - 1 && (
                    <div className="w-px flex-1 mt-2" style={{ minHeight: 24, background: `linear-gradient(to bottom, ${s.color}60, ${arr[i+1].color}60)` }} />
                  )}
                </div>
                {/* Text content — NOT overlapping the line */}
                <div className="pb-8">
                  <div className="text-[10px] font-bold mb-1" style={{ color: C.dimSlate, letterSpacing: "0.12em" }}>STEP {s.step}</div>
                  <h4 className="text-xl font-bold mb-1" style={{ color: C.white }}>{s.title}</h4>
                  <p className="text-base leading-relaxed" style={{ color: C.dimSlate }}>{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── INDUSTRIES ─── */}
      <section className="py-20 px-6 scroll-fade" style={{ background: "rgba(108,99,255,0.04)", borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-5xl mx-auto text-center">
          <div className="text-xs font-semibold mb-6 tracking-widest uppercase" style={{ color: C.dimSlate }}>Built for regulated industries</div>
          <div className="flex flex-wrap gap-3 justify-center">
            {industries.map((ind) => (
              <span key={ind} className="px-4 py-2 rounded-full text-sm font-medium" style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, color: C.slate }}>
                {ind}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ROLES ─── */}
      <section className="py-24 px-6 max-w-6xl mx-auto scroll-fade">
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-black tracking-tight mb-4" style={{ color: C.white }}>Every role gets<br />exactly what they need.</h2>
          <p className="max-w-lg mx-auto text-lg" style={{ color: C.dimSlate }}>ManagEaze is role-aware. Different users see different views, have different permissions, and get different tasks.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { role: "Policy Creator", icon: <FileText size={18} />, color: C.purple, perks: ["Draft with AI assistance", "Upload existing documents", "Submit for review"] },
            { role: "Reviewer", icon: <Search size={18} />, color: "#a78bfa", perks: ["Comment on draft policies", "Request revisions", "Escalate to approver"] },
            { role: "Approver", icon: <CheckCircle2 size={18} />, color: C.lime, perks: ["Final approval authority", "Multi-policy batch actions", "Override & version control"] },
            { role: "Compliance Officer", icon: <ShieldCheck size={18} />, color: "#34d399", perks: ["Health score dashboard", "Task assignment & closure", "Audit readiness overview"] },
            { role: "Risk Manager", icon: <AlertTriangle size={18} />, color: "#f59e0b", perks: ["Risk register & scoring", "Heat map visualisation", "Risk-to-policy traceability"] },
            { role: "Executive / Board", icon: <Star size={18} />, color: "#f472b6", perks: ["At-a-glance posture view", "Governance committee oversight", "Reporting dashboards"] },
          ].map((r, i) => (
            <motion.div
              key={i}
              className="rounded-2xl p-6"
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: i * 0.08, ease: "easeOut" }}
              style={{ background: C.cardBg, border: `1px solid ${C.border}`, backdropFilter: "blur(8px)" }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${r.color}15`, color: r.color }}>
                  {r.icon}
                </div>
                <span className="font-bold text-base" style={{ color: C.white }}>{r.role}</span>
              </div>
              <ul className="space-y-2">
                {r.perks.map((p, j) => (
                  <li key={j} className="flex items-center gap-2 text-base" style={{ color: C.dimSlate }}>
                    <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: r.color }} />
                    {p}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── TRUST SIGNALS ─── */}
      <section className="py-20 px-6 max-w-6xl mx-auto scroll-fade">
        <div className="rounded-3xl p-10 md:p-16 text-center" style={{ background: `linear-gradient(135deg, rgba(108,99,255,0.15), rgba(168,224,99,0.08))`, border: `1px solid ${C.border}` }}>
          <div className="flex flex-wrap justify-center gap-8 mb-8">
            {[
              { icon: <Lock size={20} />, label: "Role-Based Access Control" },
              { icon: <ShieldCheck size={20} />, label: "Audit Trail on Every Action" },
              { icon: <Globe size={20} />, label: "NVIDIA Inception Member" },
              { icon: <Users size={20} />, label: "Enterprise Multi-Tenancy" },
            ].map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-base font-medium" style={{ color: C.slate }}>
                <span style={{ color: C.purple }}>{t.icon}</span>
                {t.label}
              </div>
            ))}
          </div>
          <h2 className="text-4xl md:text-5xl font-black mb-4" style={{ color: C.white }}>Ready to govern without chaos?</h2>
          <p className="max-w-md mx-auto mb-8 text-lg" style={{ color: C.dimSlate }}>Join compliance-forward organisations already transforming their GRC posture with ManagEaze.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="cta-glow flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-base transition-all hover:scale-105" style={{ background: C.purple, color: C.white }}>
              Start Free Trial <ArrowRight size={16} />
            </button>
            <button className="flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-base transition-all hover:opacity-80" style={{ border: `1px solid ${C.border}`, color: C.slate }}>
              Request a Demo
            </button>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="py-12 px-8" style={{ borderTop: `1px solid ${C.border}` }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <img src={logoSrc} alt="ManagEaze" style={{ height: 28, width: "auto" }} />
          <p className="text-xs text-center" style={{ color: C.dimSlate }}>The all-in-one GRC platform · Policy, Compliance, Risk, Governance</p>
          <p className="text-xs" style={{ color: C.dimSlate }}>© 2026 ManagEaze · All rights reserved</p>
        </div>
      </footer>
    </div>
  );
}
